import { useMemo, useEffect, useState } from 'react';
import { VStack, Button } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { createFillableBlocksStore, FormStoreContext } from '@/components/fillable-blocks/common';
import BlockFromType from '@/components/fillable-blocks/block-from-type';
import api from '@/utils/api';
import { StaticField, FieldOption } from '@/lib/types';

interface ContentTypeField {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  field_options: FieldOption[];
  workspace_id: number;
  content_type_id: number;
  sort_order: number;
  properties: any;
}

interface Props {
  rootBlocks: ContentTypeField[];
  responses: any;
  contentId?: string;
}

export default function FormContent({ rootBlocks, responses, contentId }: Props) {
  const useFillableBlocksStore = useMemo(() => createFillableBlocksStore(), []);
  const navigate = useNavigate();
  const { workspaceId, contentTypeId, id } = useParams<{
    workspaceId: string;
    contentTypeId: string;
    id: string;
  }>();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null,
  );

  useEffect(() => {
    useFillableBlocksStore.setState({
      blocks: rootBlocks,
      payloadByBlock: {
        ...(responses || {}),
      },
    });
  }, [responses, contentId, rootBlocks, useFillableBlocksStore]);

  async function contentEdit() {
    try {
      const state = useFillableBlocksStore.getState();
      const { blocks, payloadByBlock } = state;

      setIsLoading(true);
      const content = await api.get(`contents/${id}`);
      const existingContent = content.data.data;
      const body = {
        workspace_id: workspaceId,
        content_type_id: contentTypeId,
        name: existingContent.name,
        description: existingContent.description,
        icon: existingContent.icon,
        is_public: existingContent.is_public,
        content: {
          ...existingContent.content,
        },
      };

      for (const block of blocks) {
        const payload = payloadByBlock[block.referenceID];
        if (!payload) {
          continue;
        }

        body.content[block.referenceID] = payload;

        if (block.field_unique_name === StaticField.Name) {
          body.name = payload.value;
          delete body.content[block.referenceID];
        }
        if (block.field_unique_name === StaticField.Description) {
          body.description = payload.value;
          delete body.content[block.referenceID];
        }
        if (block.field_unique_name === StaticField.Media) {
          body.icon = payload.value;
          delete body.content[block.referenceID];
        }
        if (block.field_unique_name === StaticField.IsPublic) {
          body.is_public = payload.value;
          delete body.content[block.referenceID];
        }
      }

      await api.patch(`/contents/${id}`, body);

      toaster.create({
        title: 'Content saved successfully.',
        type: 'success',
        duration: 3000,
      });
      navigate(
        `/${workspaceId}/content-types/${contentTypeId}/contents/my-library?ref=${existingContent.reference_id}`,
      );
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error || 'Failed to save content',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function contentCreate() {
    if (!workspaceId || !contentTypeId) {
      return;
    }

    setIsLoading(true);
    try {
      const state = useFillableBlocksStore.getState();
      const { blocks, payloadByBlock } = state;
      const body = {
        status: 'draft',
        workspace_id: parseInt(workspaceId, 10),
        content_type_id: parseInt(contentTypeId, 10),
        name: '',
        description: '',
        is_public: false,
        content: {} as any,
      };
      let files = [];

      for (const block of blocks) {
        const payload = payloadByBlock[block.referenceID];
        if (!payload) {
          continue;
        }

        body.content[block.referenceID] = payload;

        if (block.field_unique_name === StaticField.Name) {
          body.name = payload.value;
          delete body.content[block.referenceID];
        }
        if (block.field_unique_name === StaticField.Description) {
          body.description = payload.value;
          delete body.content[block.referenceID];
        }
        if (block.field_unique_name === StaticField.IsPublic) {
          body.is_public = payload.value;
          delete body.content[block.referenceID];
        }
        if (block.field_unique_name === StaticField.Media) {
          files = payload.value;
          delete body.content[block.referenceID];
        }
      }

      const res = await api.post(`/contents`, body);

      if (files.length > 0) {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          setUploadProgress({ current: i + 1, total: files.length });
          try {
            const formData = new FormData();
            formData.append('file', file);
            if (file.type.includes('video')) {
              await api.post(`/media/contents/${res.data.data.id}/video`, formData);
            } else {
              await api.post(`/media/contents/${res.data.data.id}/image`, formData);
            }
          } catch (err: any) {
            toaster.create({
              type: 'error',
              title: err.response?.data?.error || 'Failed to upload media',
              duration: 3000,
            });
            continue;
          }
        }
      }

      toaster.create({
        title: 'Content saved successfully.',
        type: 'success',
        duration: 3000,
      });

      navigate(
        `/${workspaceId}/content-types/${contentTypeId}/contents/my-library?ref=${res.data.data.reference_id}`,
      );
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error || 'Failed to save content',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
    }
  }

  return (
    <FormStoreContext.Provider value={useFillableBlocksStore}>
      <VStack gap={4}>
        {rootBlocks.map((block) => (
          <BlockFromType key={block.referenceID} block={block} contentId={id} />
        ))}
        <Button
          colorPalette='brand'
          w={'full'}
          size={'md'}
          mt={10}
          mb={14}
          loading={isLoading}
          loadingText={
            uploadProgress
              ? `Uploading media (${uploadProgress.current}/${uploadProgress.total})…`
              : undefined
          }
          onClick={id ? contentEdit : contentCreate}
        >
          Save
        </Button>
      </VStack>
    </FormStoreContext.Provider>
  );
}
